import Phaser from 'phaser';
import { TILE_SIZE, MAP_WIDTH, MAP_HEIGHT } from '../data/world-config';
import { MONSTERS, MONSTER_WAVES, type MonsterSpecies } from '../data/monsters';
import type { GameScene } from '../scenes/GameScene';

export interface ActiveMonster {
  id: string;
  species: MonsterSpecies;
  sprite: Phaser.Physics.Arcade.Sprite;
  hp: number;
  maxHp: number;
  hpBar: Phaser.GameObjects.Graphics;
  lastAttackTime: number;
  dead: boolean;
  stunned: boolean;
  stunEndTime: number;
}

export class NightMonsterSystem {
  private scene: GameScene;
  monsters: ActiveMonster[] = [];
  private spawnTimer: number = 0;
  private wasNight: boolean = false;
  nightsSurvived: number = 0;
  private monsterIdCounter: number = 0;
  private attackCooldown: number = 1500;

  // Structured night wave system
  private nightPhase: 'scout' | 'calm1' | 'main' | 'calm2' | 'final' | 'done' = 'done';
  private phaseTimer: number = 0;
  private phaseSpawnTimer: number = 0;
  private monstersSpawnedThisPhase: number = 0;
  monstersKilledTonight: number = 0;

  // Daytime scout spawning
  private dayScoutTimer: number = 0;
  private dayScoutInterval: number = 20000;
  private maxDayScouts: number = 2;

  // Night wave budgets per day range
  private static readonly WAVE_BUDGETS: { minDay: number; scout: number; main: number; final: number }[] = [
    { minDay: 1, scout: 2, main: 3, final: 2 },
    { minDay: 3, scout: 3, main: 5, final: 3 },
    { minDay: 5, scout: 3, main: 7, final: 4 },
    { minDay: 8, scout: 4, main: 9, final: 5 },
    { minDay: 12, scout: 4, main: 10, final: 6 },
  ];

  // Phase durations in ms (summer: compressed to fit ~2.5 min night)
  private static readonly PHASE_DURATIONS = {
    scout: 30_000,   // 30 seconds
    calm1: 10_000,   // 10 second breather
    main: 50_000,    // 50 seconds
    calm2: 10_000,   // 10 second breather
    final: 30_000,   // 30 seconds
  };

  constructor(scene: GameScene) {
    this.scene = scene;
  }

  update(time: number, delta: number): void {
    const isNight = this.scene.dayNightSystem.isNight();

    if (isNight && !this.wasNight) {
      this.onNightStart();
    }

    if (!isNight && this.wasNight) {
      this.onNightEnd();
    }

    this.wasNight = isNight;

    if (isNight && this.nightPhase !== 'done') {
      this.updateNightPhase(delta);
    } else if (!isNight) {
      // Day: occasional scout monsters
      const dayCount = this.scene.dayNightSystem.getDayCount();
      if (dayCount >= 2) {
        this.dayScoutTimer += delta;
        if (this.dayScoutTimer >= this.dayScoutInterval) {
          this.dayScoutTimer = 0;
          this.spawnDayScout(dayCount);
        }
      }
    }

    // Update each monster AI
    for (const monster of this.monsters) {
      if (monster.dead) continue;
      this.updateMonsterAI(monster, time);
    }

    // Clean up dead monsters
    this.monsters = this.monsters.filter(m => !m.dead);
  }

  private onNightStart(): void {
    this.monstersKilledTonight = 0;
    this.scene.events.emit('notification', 'Night falls... monsters approach! Sentinels and sword ready!');
    this.nightPhase = 'scout';
    this.phaseTimer = 0;
    this.phaseSpawnTimer = 0;
    this.monstersSpawnedThisPhase = 0;

    // First spawn after a brief delay
    this.scene.time.delayedCall(2000, () => {
      if (this.scene.dayNightSystem.isNight()) {
        this.spawnWaveMonsters();
      }
    });
  }

  private onNightEnd(): void {
    this.nightsSurvived++;
    this.nightPhase = 'done';
    for (const monster of this.monsters) {
      this.killMonster(monster, false);
    }
    this.monsters = [];
    this.scene.events.emit('notification', 'Dawn breaks! The monsters retreat.');
    this.scene.events.emit('night-survived', this.nightsSurvived);
    this.scene.events.emit('night-summary', {
      monstersKilled: this.monstersKilledTonight,
      nightsSurvived: this.nightsSurvived,
    });
  }

  private updateNightPhase(delta: number): void {
    this.phaseTimer += delta;
    this.phaseSpawnTimer += delta;

    const durations = NightMonsterSystem.PHASE_DURATIONS;
    const phaseDuration = durations[this.nightPhase as keyof typeof durations] ?? 60_000;

    // Transition to next phase when time is up
    if (this.phaseTimer >= phaseDuration) {
      this.advancePhase();
      return;
    }

    // During calm phases, don't spawn
    if (this.nightPhase === 'calm1' || this.nightPhase === 'calm2') return;

    // Spawn intervals vary by phase (tighter for summer's shorter nights)
    const spawnInterval = this.nightPhase === 'scout' ? 6000
      : this.nightPhase === 'main' ? 4500
      : 3500; // final push spawns faster

    if (this.phaseSpawnTimer >= spawnInterval) {
      this.phaseSpawnTimer = 0;
      this.spawnWaveMonsters();
    }
  }

  private advancePhase(): void {
    this.phaseTimer = 0;
    this.phaseSpawnTimer = 0;
    this.monstersSpawnedThisPhase = 0;

    switch (this.nightPhase) {
      case 'scout':
        this.nightPhase = 'calm1';
        this.scene.events.emit('notification', 'A brief calm... prepare your defenses!');
        break;
      case 'calm1':
        this.nightPhase = 'main';
        this.scene.events.emit('notification', 'The main wave approaches!');
        break;
      case 'main':
        this.nightPhase = 'calm2';
        this.scene.events.emit('notification', 'The horde thins... one more push!');
        break;
      case 'calm2':
        this.nightPhase = 'final';
        this.scene.events.emit('notification', 'Final wave incoming!');
        break;
      case 'final':
        this.nightPhase = 'done';
        break;
    }
  }

  private getWaveBudget(): { scout: number; main: number; final: number } {
    const dayCount = this.scene.dayNightSystem.getDayCount();
    let budget = NightMonsterSystem.WAVE_BUDGETS[0];
    for (const b of NightMonsterSystem.WAVE_BUDGETS) {
      if (dayCount >= b.minDay) budget = b;
    }
    return budget;
  }

  private spawnWaveMonsters(): void {
    const dayCount = this.scene.dayNightSystem.getDayCount();
    const budget = this.getWaveBudget();

    // Determine max for this phase
    const phaseMax = this.nightPhase === 'scout' ? budget.scout
      : this.nightPhase === 'main' ? budget.main
      : budget.final;

    if (this.monstersSpawnedThisPhase >= phaseMax) return;

    // Get available monster pool
    let waveTier = MONSTER_WAVES[0];
    for (const wave of MONSTER_WAVES) {
      if (dayCount >= wave.minDay) waveTier = wave;
    }

    // Limit concurrent alive monsters
    const aliveCount = this.monsters.filter(m => !m.dead).length;
    if (aliveCount >= phaseMax + 2) return;

    // Spawn 1-2 monsters per tick
    const count = Math.min(
      1 + Math.floor(Math.random() * 2),
      phaseMax - this.monstersSpawnedThisPhase
    );

    for (let i = 0; i < count; i++) {
      // During final phase on Day 5+, add mini-boss chance
      if (this.nightPhase === 'final' && dayCount >= 5 && i === 0 && Math.random() < 0.3) {
        if (dayCount >= 12) {
          this.spawnMonster('root_titan');
        } else {
          this.spawnMonster('fungal_brute');
        }
      } else {
        // Scout phase prefers fast enemies
        if (this.nightPhase === 'scout') {
          const scoutPool = dayCount < 3 ? ['thorn_crawler', 'piranha_plant'] : ['thorn_crawler'];
          this.spawnMonster(scoutPool[Math.floor(Math.random() * scoutPool.length)]);
        } else {
          const monsterId = waveTier.monsters[Math.floor(Math.random() * waveTier.monsters.length)];
          this.spawnMonster(monsterId);
        }
      }
      this.monstersSpawnedThisPhase++;
    }
  }

  /** Spawn a lone scout monster during daytime (so players can practice combat). */
  private spawnDayScout(dayCount: number): void {
    // Only spawn if few monsters exist
    const aliveCount = this.monsters.filter(m => !m.dead).length;
    if (aliveCount >= this.maxDayScouts) return;

    // 40% chance per interval
    if (Math.random() > 0.4) return;

    // Pick a weaker monster for daytime
    const scoutPool = dayCount < 5
      ? ['thorn_crawler']
      : ['thorn_crawler', 'piranha_plant'];
    const monsterId = scoutPool[Math.floor(Math.random() * scoutPool.length)];

    this.spawnMonster(monsterId);
    this.scene.events.emit('notification', 'A monster scout appeared nearby!');
  }

  private spawnMonster(speciesId: string): void {
    const species = MONSTERS[speciesId];
    if (!species) return;

    const side = Math.floor(Math.random() * 4);
    let spawnX: number;
    let spawnY: number;
    const playerX = this.scene.player.sprite.x;
    const playerY = this.scene.player.sprite.y;

    const margin = 60;
    switch (side) {
      case 0:
        spawnX = playerX + Phaser.Math.Between(-200, 200);
        spawnY = playerY - Phaser.Math.Between(150, 300);
        break;
      case 1:
        spawnX = playerX + Phaser.Math.Between(150, 300);
        spawnY = playerY + Phaser.Math.Between(-200, 200);
        break;
      case 2:
        spawnX = playerX + Phaser.Math.Between(-200, 200);
        spawnY = playerY + Phaser.Math.Between(150, 300);
        break;
      default:
        spawnX = playerX - Phaser.Math.Between(150, 300);
        spawnY = playerY + Phaser.Math.Between(-200, 200);
        break;
    }

    spawnX = Phaser.Math.Clamp(spawnX, margin, MAP_WIDTH * TILE_SIZE - margin);
    spawnY = Phaser.Math.Clamp(spawnY, margin, MAP_HEIGHT * TILE_SIZE - margin);

    const sprite = this.scene.physics.add.sprite(spawnX, spawnY, 'monsters', species.spriteRow * 4);
    sprite.setDepth(9);
    sprite.setSize(12, 12);
    sprite.setOffset(2, 4);

    const hpBar = this.scene.add.graphics();
    hpBar.setDepth(11);

    const id = `monster_${this.monsterIdCounter++}`;
    const monster: ActiveMonster = {
      id,
      species,
      sprite,
      hp: species.hp,
      maxHp: species.hp,
      hpBar,
      lastAttackTime: 0,
      dead: false,
      stunned: false,
      stunEndTime: 0,
    };

    this.monsters.push(monster);

    // Spawn-in effect: warning circle + fade in
    sprite.setAlpha(0);

    // Red warning circle on ground
    const warning = this.scene.add.circle(spawnX, spawnY, 12, 0xff4444, 0.3);
    warning.setDepth(8);
    warning.setStrokeStyle(1, 0xff4444, 0.6);
    this.scene.tweens.add({
      targets: warning,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 800,
      onComplete: () => warning.destroy(),
    });

    this.scene.tweens.add({
      targets: sprite,
      alpha: 1,
      duration: 500,
      ease: 'Power2',
    });
  }

  private updateMonsterAI(monster: ActiveMonster, time: number): void {
    const sprite = monster.sprite;
    const playerSprite = this.scene.player.sprite;

    // Check stun
    if (monster.stunned) {
      if (time >= monster.stunEndTime) {
        monster.stunned = false;
        sprite.clearTint();
      } else {
        sprite.setVelocity(0, 0);
        return;
      }
    }

    // Find best target: player or nearest sentinel
    let targetX = playerSprite.x;
    let targetY = playerSprite.y;
    let targetType: 'player' | 'sentinel' = 'player';
    let targetPlotKey: string | undefined;

    const distToPlayer = Math.sqrt(
      (playerSprite.x - sprite.x) ** 2 + (playerSprite.y - sprite.y) ** 2
    );

    // Check if any sentinel is closer than the player
    if (this.scene.plantSentinelSystem) {
      const sentinels = this.scene.plantSentinelSystem.getSentinelPositions();
      let closestSentinelDist = distToPlayer;

      for (const s of sentinels) {
        const sd = Math.sqrt((s.x - sprite.x) ** 2 + (s.y - sprite.y) ** 2);
        if (sd < closestSentinelDist) {
          closestSentinelDist = sd;
          targetX = s.x;
          targetY = s.y;
          targetType = 'sentinel';
          targetPlotKey = s.plotKey;
        }
      }
    }

    const dx = targetX - sprite.x;
    const dy = targetY - sprite.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 16) {
      // Apply slow effect from sentinel snare if present
      let speed = monster.species.speed;
      const slowedSpeed = (monster as ActiveMonster & { _slowedSpeed?: number })._slowedSpeed;
      if (slowedSpeed !== undefined) {
        speed = slowedSpeed;
      }

      const vx = (dx / dist) * speed;
      const vy = (dy / dist) * speed;
      sprite.setVelocity(vx, vy);

      // Animate walking
      const frameOffset = Math.floor(time / 200) % 4;
      sprite.setFrame(monster.species.spriteRow * 4 + frameOffset);
      sprite.setFlipX(dx < 0);
    } else {
      sprite.setVelocity(0, 0);

      if (time - monster.lastAttackTime > this.attackCooldown) {
        monster.lastAttackTime = time;

        if (targetType === 'sentinel' && targetPlotKey && this.scene.plantSentinelSystem) {
          // Attack the sentinel plant
          this.scene.plantSentinelSystem.destroySentinel(targetPlotKey);
        } else {
          // Attack the player
          this.monsterAttackPlayer(monster);
        }
      }
    }

    this.drawMonsterHpBar(monster);
  }

  private monsterAttackPlayer(monster: ActiveMonster): void {
    let damage = monster.species.attackPower;

    // Sentinel shield aura reduces damage
    if (this.scene.plantSentinelSystem) {
      const shieldReduction = this.scene.plantSentinelSystem.getShieldReduction();
      if (shieldReduction > 0) {
        damage = Math.max(1, Math.floor(damage * (1 - shieldReduction)));
      }
    }

    this.scene.events.emit('player-damaged', damage);

    // Visual feedback: flash player red
    this.scene.player.sprite.setTint(0xff4444);
    this.scene.time.delayedCall(200, () => {
      // Only clear if not blocking (combat system may have set tint)
      if (!this.scene.combatSystem.isBlocking) {
        this.scene.player.sprite.clearTint();
      }
    });

    // Camera shake
    this.scene.cameras.main.shake(100, 0.005);

    // Show damage number on player
    const text = this.scene.add.text(
      this.scene.player.sprite.x,
      this.scene.player.sprite.y - 10,
      `-${damage}`,
      {
        fontFamily: 'monospace',
        fontSize: '9px',
        color: '#ff4444',
        stroke: '#000000',
        strokeThickness: 3,
        fontStyle: 'bold',
      }
    );
    text.setOrigin(0.5, 1);
    text.setDepth(20);
    this.scene.tweens.add({
      targets: text,
      y: text.y - 20,
      alpha: 0,
      duration: 700,
      onComplete: () => text.destroy(),
    });
  }

  /** Enhanced hit handling with knockback amount and attack type. */
  hitMonster(monster: ActiveMonster, damage: number, knockback: number = 120, attackType: string = 'light'): void {
    monster.hp -= damage;

    // Knockback (varies by attack type)
    const dx = monster.sprite.x - this.scene.player.sprite.x;
    const dy = monster.sprite.y - this.scene.player.sprite.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    monster.sprite.setVelocity((dx / dist) * knockback, (dy / dist) * knockback);

    // Stun duration based on attack type
    const stunDuration = attackType === 'special' ? 500 : attackType === 'heavy' ? 350 : 150;
    monster.stunned = true;
    monster.stunEndTime = this.scene.time.now + stunDuration;

    this.scene.time.delayedCall(stunDuration, () => {
      if (!monster.dead) {
        monster.sprite.setVelocity(0, 0);
      }
    });

    // Flash color based on attack type
    const flashColors: Record<string, number> = {
      light: 0xffffff,
      heavy: 0xffaa00,
      special: 0x44aaff,
    };
    monster.sprite.setTint(flashColors[attackType] ?? 0xffffff);
    this.scene.time.delayedCall(100, () => {
      if (!monster.dead && !monster.stunned) {
        monster.sprite.clearTint();
      } else if (!monster.dead && monster.stunned) {
        monster.sprite.setTint(0xaaaaaa); // grey tint while stunned
      }
    });

    // Screen shake scales with attack type
    if (attackType === 'heavy') {
      this.scene.cameras.main.shake(80, 0.004);
    } else if (attackType === 'special') {
      this.scene.cameras.main.shake(120, 0.006);
    }

    if (monster.hp <= 0) {
      this.killMonster(monster, true);
    }
  }

  private killMonster(monster: ActiveMonster, giveRewards: boolean): void {
    monster.dead = true;

    if (giveRewards) {
      this.monstersKilledTonight++;
      this.scene.events.emit('monster-killed', {
        xp: monster.species.xpReward,
        coins: monster.species.coinReward,
        speciesId: monster.species.id,
      });
    }

    // Enhanced death animation
    const sprite = monster.sprite;

    // Death burst particles
    if (giveRewards) {
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI * 2 * i) / 6;
        const particle = this.scene.add.circle(
          sprite.x, sprite.y, 2, 0xff4444, 0.8
        );
        particle.setDepth(15);
        this.scene.tweens.add({
          targets: particle,
          x: sprite.x + Math.cos(angle) * 25,
          y: sprite.y + Math.sin(angle) * 25,
          alpha: 0,
          duration: 400,
          onComplete: () => particle.destroy(),
        });
      }
    }

    this.scene.tweens.add({
      targets: sprite,
      alpha: 0,
      scaleX: 0.3,
      scaleY: 0.3,
      angle: giveRewards ? 180 : 90,
      duration: giveRewards ? 500 : 400,
      onComplete: () => {
        sprite.destroy();
        monster.hpBar.destroy();
      },
    });
  }

  private drawMonsterHpBar(monster: ActiveMonster): void {
    const g = monster.hpBar;
    g.clear();

    const x = monster.sprite.x - 8;
    const y = monster.sprite.y - 12;
    const w = 16;
    const h = 2;

    // Background
    g.fillStyle(0x333333);
    g.fillRect(x, y, w, h);

    // HP fill
    const ratio = monster.hp / monster.maxHp;
    const color = ratio > 0.5 ? 0x6aaa3a : ratio > 0.25 ? 0xe8a038 : 0xd4573b;
    g.fillStyle(color);
    g.fillRect(x, y, w * ratio, h);

    // Stun indicator
    if (monster.stunned) {
      g.fillStyle(0xffff00);
      g.fillRect(x, y - 2, w * 0.3, 1);
    }
  }

  getMonsterAt(x: number, y: number, radius: number = 20): ActiveMonster | null {
    for (const monster of this.monsters) {
      if (monster.dead) continue;
      const dx = monster.sprite.x - x;
      const dy = monster.sprite.y - y;
      if (Math.sqrt(dx * dx + dy * dy) <= radius) {
        return monster;
      }
    }
    return null;
  }

  getAliveMonsters(): ActiveMonster[] {
    return this.monsters.filter(m => !m.dead);
  }
}
